import { sanitizeHtmlContent } from '@risksmart-app/shared/src/htmlSanitizer';

/**
 * Sanitises rich-text widget content, on the way into settings and on the way out
 * to the renderer.
 *
 * Deliberately uses the platform rich-text sanitiser rather than a bare
 * `sanitizeHtml()` call. The `sanitize-html` default allowlist permits no `style`
 * attribute, which silently dropped TinyMCE's `background-color` (the highlight
 * button) and `text-align` (the alignment buttons) while leaving `<strong>` intact —
 * so bold persisted and colour and alignment did not (CUS-398).
 */
export const sanitiseRichTextContent = (content: string): string =>
  sanitizeHtmlContent(content);

/**
 * Builds the HTML handed to the Highcharts renderer: sanitised content wrapped in a
 * block span that constrains it to the plot area. The wrapper is added after
 * sanitisation so its own layout styles are not subject to the content allowlist.
 *
 * The explicit pixel `width` is what makes `text-align` work, and it has to live here
 * rather than being passed to the element's `css()`. Highcharts intercepts a `width`
 * style, converts it to its internal `textWidth`, and then only applies a real width
 * when the text both contains whitespace/a hyphen and is already wider than the box
 * (`Core/Renderer/HTML/HTMLElement.js`) — otherwise it sets `width: auto`. That
 * mechanism exists to wrap long text, so it can never produce a box wider than its
 * content, which is exactly what aligning within it requires. Setting the width on our
 * own span sidesteps it (CUS-398).
 *
 * `white-space: normal` is likewise required: Highcharts forces `nowrap` on the element
 * it creates, and that inherits into this wrapper and would suppress wrapping.
 */
export const buildRichTextWidgetHtml = (
  content: string | undefined,
  plotWidth: number
): string =>
  `<span style="display: block; width: ${plotWidth}px; max-width: 100%; white-space: normal; overflow-wrap: break-word; text-wrap: auto;">${sanitiseRichTextContent(
    content ?? ''
  )}</span>`;

/**
 * Builds the css applied to the Highcharts text element holding the widget content.
 *
 * Deliberately does NOT set `width`. Highcharts would convert it to `textWidth`, which
 * cannot widen the box beyond its content (see `buildRichTextWidgetHtml`); the layout
 * width lives on the wrapper span instead. Wrapping is handled there too, so this only
 * bounds the height and lets the content scroll.
 */
export const buildRichTextWidgetCss = (
  plotHeight: number
): Record<string, string> => ({
  overflow: 'auto',
  maxHeight: `${plotHeight}px`,
});
